// common
import {
  VuexModule,
  Module,
  Mutation,
  getModule,
  Action,
} from "vuex-module-decorators";
import store from "@/store";

// api
import { getPosCheckInConfig } from "@/api/check-in/check-in";

// types
import {
  CheckInMode,
  IBookingCheckedIn,
  ICheckInConfig,
} from "@/types/check-in";

@Module({ dynamic: true, namespaced: true, store, name: "CheckIn" })
class CheckIn extends VuexModule {
  public areaId: number | null = null;
  public areaCode: string | null = null;
  public areaName: string | null = null;
  public mode: CheckInMode | null = null;
  public autoScanOnSale = false;

  /**
   * Counts of the orders checked in on this terminal, by order reference. The
   * ticket index needs a moment to catch up, so the Orders table reads these
   * first and the operator sees the entry they just recorded.
   */
  public orderCounts: Record<string, IBookingCheckedIn> = {};

  @Mutation
  public setOrderCount(payload: IBookingCheckedIn & { reference: string }) {
    this.orderCounts = {
      ...this.orderCounts,
      [payload.reference]: {
        checkedIn: payload.checkedIn,
        total: payload.total,
      },
    };
  }

  @Mutation
  public clearOrderCount(reference: string) {
    const { [reference]: removed, ...rest } = this.orderCounts;

    void removed;
    this.orderCounts = rest;
  }

  @Mutation
  public setConfig(config: ICheckInConfig) {
    this.areaId = config.areaId;
    this.areaCode = config.areaCode;
    this.areaName = config.areaName;
    this.mode = config.mode;
    this.autoScanOnSale = config.autoScanOnSale;
  }

  /**
   * Get the area of a POS configuration. The POS config load calls this.
   * The caller keeps the last area if the request fails.
   *
   * @param {object} payload the group and the POS configuration.
   * @returns {Promise<void>}
   */
  @Action
  public async loadConfig(payload: {
    groupId: number;
    posConfigId: number;
  }): Promise<void> {
    this.setConfig(
      await getPosCheckInConfig(payload.groupId, payload.posConfigId)
    );
  }

  @Action
  public restore(fields: Partial<ICheckInConfig>) {
    this.setConfig({
      areaId: null,
      areaCode: null,
      areaName: null,
      mode: null,
      autoScanOnSale: false,
      ...fields,
    });
  }
}

export default getModule(CheckIn);
